import React, { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import minigridAPI from '../../minigrid/service/minigridAPI'
import { FleetUiStrings } from '../i18n/fleetI18n'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'

interface MinigridPickerProps {
  currentMinigridIds: string[]
  onAdd: (minigridId: string) => void
  disabled?: boolean
  strings: FleetUiStrings
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px',
  borderBottom: '1px solid #f1f5f9',
  fontSize: 13,
}

// Picker that lists minigrids NOT already linked to this fleet. A minigrid can
// be linked to many fleets (non-exclusive), so there is no cross-fleet filter
// here — only the already-linked ones are hidden. Lists all minigrids with the
// app convention pageSize 0 = all.
const MinigridPicker: React.FC<MinigridPickerProps> = ({
  currentMinigridIds,
  onAdd,
  disabled,
  strings,
}) => {
  const [search, setSearch] = useState('')
  const minigridsQuery = useQuery({
    queryKey: ['minigrids', 'all'],
    queryFn: () => minigridAPI.getAllMinigrids(0, 0),
    staleTime: 30_000,
  })
  const linkedSet = useMemo(
    () => new Set(currentMinigridIds),
    [currentMinigridIds],
  )

  // Mémoïsé : `?? []` crée une nouvelle référence à chaque rendu, ce qui ferait
  // ré-exécuter le useMemo `filtered` en boucle (react-hooks/exhaustive-deps).
  const minigrids = useMemo(
    () => minigridsQuery.data?.data ?? [],
    [minigridsQuery.data],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return minigrids.filter((m) => {
      if (linkedSet.has(m.minigrid_id)) return false // already linked
      if (q === '') return true
      return (
        (m.minigrid_name || '').toLowerCase().includes(q) ||
        (m.minigrid_id || '').toLowerCase().includes(q)
      )
    })
  }, [minigrids, search, linkedSet])

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        maxWidth: 420,
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={strings.searchMinigrid}
          style={{
            width: '100%',
            height: 30,
            padding: '2px 8px',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            fontSize: 13,
          }}
        />
      </div>
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {minigridsQuery.isLoading && (
          <div style={{ ...rowStyle, color: '#6b7280' }}>…</div>
        )}
        {!minigridsQuery.isLoading && filtered.length === 0 && (
          <div style={{ ...rowStyle, color: '#6b7280' }}>—</div>
        )}
        {filtered.map((m) => (
          <div key={m.minigrid_id} style={rowStyle}>
            <span style={{ color: '#1f2937' }}>
              {m.minigrid_name || m.minigrid_id}
              <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                {m.minigrid_id}
              </span>
            </span>
            <ButtonV2
              type="button"
              onClick={() => onAdd(m.minigrid_id)}
              disabled={disabled}
              variant="contained"
              color="primary"
              size="small"
            >
              +
            </ButtonV2>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MinigridPicker
